import React, { useState, useEffect, useRef } from 'react';
import { Edit2 } from 'lucide-react';
import { cn } from "@/lib/utils";

interface VisualTextEditableProps {
    value: string;
    onSave: (newValue: string) => void;
    isEditMode: boolean;
    className?: string;
    placeholder?: string;
    multiline?: boolean;
    as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span' | 'div';
}

export const VisualTextEditable: React.FC<VisualTextEditableProps> = ({
    value,
    onSave,
    isEditMode,
    className,
    placeholder = "Click to edit...",
    multiline = false,
    as: Component = 'div'
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(value);
    const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

    useEffect(() => {
        setEditValue(value);
    }, [value]);

    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isEditing]);

    const handleSave = () => {
        setIsEditing(false);
        if (editValue !== value) {
            onSave(editValue);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !multiline) {
            handleSave();
        }
        if (e.key === 'Escape') {
            setEditValue(value);
            setIsEditing(false);
        }
    };

    if (!isEditMode) {
        return (
            <Component className={className}>
                {value}
            </Component>
        );
    }

    if (isEditing) {
        return multiline ? (
            <textarea
                ref={inputRef as React.RefObject<HTMLTextAreaElement>}
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onBlur={handleSave}
                onKeyDown={handleKeyDown}
                className={cn(
                    "w-full bg-white/90 text-black p-2 rounded outline-none border-2 border-purple-500 shadow-lg min-h-[100px] resize-none",
                    className
                )}
            />
        ) : (
            <input
                ref={inputRef as React.RefObject<HTMLInputElement>}
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onBlur={handleSave}
                onKeyDown={handleKeyDown}
                className={cn(
                    "w-full bg-white/90 text-black px-2 py-1 rounded outline-none border-2 border-purple-500 shadow-lg",
                    className
                )}
            />
        );
    }

    return (
        <div 
            className="relative group cursor-pointer inline-block"
            onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsEditing(true);
            }}
        >
            <Component className={cn(
                "relative z-10 decoration-purple-500/50 decoration-wavy",
                className
            )}>
                {value || <span className="text-gray-400 italic">{placeholder}</span>}
            </Component>
            
            {/* Hover Indicator */}
            <div className="absolute -inset-2 border-2 border-dashed border-purple-500/0 group-hover:border-purple-500/50 rounded-lg transition-all z-0 pointer-events-none flex items-start justify-end p-1">
                <div className="bg-purple-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity scale-75">
                    <Edit2 size={12} />
                </div>
            </div>
        </div>
    );
};
